import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  Text,
} from '@chakra-ui/react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CompositionGroup } from '@texas/api/endpoints/compositionGroup/compositionGroupApi';
import {
  MatrixAxis,
  compositionGroupMatrixApi,
} from '@texas/api/endpoints/compositionGroup/compositionGroupMatrixApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { ErrorDetails } from '@texas/components/shared/alert/ErrorDetails';
import { compositionEvents } from '@texas/components/shared/composition/events';
import { metricShortName } from '@texas/components/shared/composition/dimension/translations';
import { ensureEnumNumber } from '@texas/utils/helpers/enumHelpers';
import { CompositionName } from '../../shared/CompositionName';
import { SharedDisclosureProps } from '@texas/components/shared/types';
import { Composition } from '@texas/api/endpoints/compositionApi';

interface Props extends SharedDisclosureProps {
  groups: CompositionGroup[];
  targetGroup: CompositionGroup;
  targetCompositionId: number;
  controllerId: number;
}

interface SourceCardProps {
  group: CompositionGroup;
  composition: Composition;
  index: number;
  isDisabled: boolean;
}

export function CopyDimensionsFromModal({ isOpen, onClose, ...props }: Props) {
  const { t } = useTranslation();
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      closeOnOverlayClick={false}
      size="2xl"
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalCloseButton />
        <ModalHeader>{t('composition.copyFrom')}</ModalHeader>
        <Form {...props} onClose={onClose} />
      </ModalContent>
    </Modal>
  );
}

function Form({
  groups,
  targetGroup,
  targetCompositionId,
  controllerId,
  onClose,
}: Omit<Props, 'isOpen'>) {
  const { t } = useTranslation();
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const { request, loading, error } = useApiRequest(
    compositionGroupMatrixApi.copyDimensionsFromCell,
  );
  const candidates = useMemo(() => {
    const target = targetGroup.compositions.find(
      (composition) => composition.id === targetCompositionId,
    );
    const targetDimensions = target?.dimensionGroup?.dimensions ?? [];
    return groups.flatMap((group) =>
      group.compositions
        .map((composition, index) => ({ group, composition, index }))
        .filter(({ composition }) => {
          const dimensions = composition.dimensionGroup?.dimensions ?? [];
          return (
            composition.id !== targetCompositionId &&
            dimensions.length > 0 &&
            dimensions.length === targetDimensions.length &&
            dimensions.every((dimension) =>
              targetDimensions.some((item) => item.id === dimension.id),
            )
          );
        }),
    );
  }, [groups, targetGroup.compositions, targetCompositionId]);
  const source = candidates.find(
    ({ composition }) => composition.id === selectedId,
  );

  const copy = async () => {
    if (!source || loading) return;
    const response = await request(targetGroup.id, {
      sourceCompositionId: source.composition.id,
      targetCompositionId,
    });
    if (!response.hasError) {
      compositionEvents.onDimensionsCopied.dispatch(controllerId);
      onClose();
    }
  };
  return (
    <>
      <ModalBody>
        <ErrorDetails error={error} />
        <Text mb={4}>
          {t('composition.chooseDimensionsSource', {
            name: getSizeLabel(targetGroup),
          })}
        </Text>
        {targetGroup.isReady && (
          <Alert status="warning" mb={4}>
            <AlertIcon />
            {t('composition.copyAndRevertIsReadyDimension')}
          </Alert>
        )}
        {!candidates.length && (
          <Text>{t('composition.noDimensionsSource')}</Text>
        )}
        <RadioGroup
          value={selectedId?.toString() ?? ''}
          onChange={(value) => setSelectedId(Number(value))}
        >
          <Flex direction="column" gap={3}>
            {candidates.map((candidate) => (
              <SourceCard
                key={candidate.composition.id}
                {...candidate}
                isDisabled={loading}
              />
            ))}
          </Flex>
        </RadioGroup>
      </ModalBody>
      <ModalFooter gap={3}>
        <Button onClick={onClose} isDisabled={loading}>
          {t('general.cancel')}
        </Button>
        <Button
          variant="texas-solid"
          onClick={copy}
          isDisabled={!source}
          isLoading={loading}
        >
          {t('composition.copyDimensions')}
        </Button>
      </ModalFooter>
    </>
  );
}

function SourceCard({
  group,
  composition,
  index,
  isDisabled,
}: SourceCardProps) {
  return (
    <Box borderWidth="1px" borderRadius="md" p={3}>
      <Radio value={composition.id.toString()} isDisabled={isDisabled} w="full">
        <Text fontWeight="bold">{getSizeLabel(group)}</Text>
        <CompositionName note={composition.note} index={index + 1} />
        <Text fontSize="sm">
          {composition.dimensionGroup?.dimensions
            .map(
              (dimension) =>
                `${dimension.label}: ${dimension.value} ${metricShortName(
                  dimension.metric,
                )}`,
            )
            .join(' · ')}
        </Text>
      </Radio>
    </Box>
  );
}

function getSizeLabel(group: CompositionGroup) {
  return ensureEnumNumber(MatrixAxis, group.colorAxis) === MatrixAxis.Columns
    ? group.matrixNodeYValue
    : group.matrixNodeXValue;
}
